/**
 * "How the pieces fit" — the vocabulary category.
 *
 * If somebody only reads one article in the whole Help Center, it should be
 * `the-big-picture`. Almost every confusion a new user has traces back to not
 * knowing the difference between a property and a parcel.
 */

import type { HelpArticle } from '../types';

export const basicsArticles: HelpArticle[] = [
  {
    slug: 'the-big-picture',
    title: 'Client, property, parcel — what is the difference?',
    summary: 'These three words look interchangeable and are not. This is the single most useful thing to understand about the app.',
    category: 'basics',
    keywords: ['hierarchy', 'structure', 'difference between', 'client vs property', 'parcel vs property', 'model', 'vocabulary', 'terms', 'jargon', 'confused'],
    related: ['what-is-an-appeal', 'what-is-a-parcel-value', 'adding-a-client'],
    body: [
      {
        kind: 'text',
        text: 'Almost everything in this app hangs off a chain of three things. They nest inside each other, biggest first.',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Client', meaning: 'The person or company that hired you. They sign the agreement and they pay your invoice. Example: Bluebonnet Property Group.' },
          { term: 'Property', meaning: 'A building or a site that the client owns. It has a street address you could drive to. Example: 400 Congress Avenue.' },
          { term: 'Parcel', meaning: 'A piece of land as the government has it recorded, with its own ID number. This is the thing that actually gets taxed, and the thing you actually appeal.' },
        ],
      },
      {
        kind: 'text',
        text: 'So: a client owns properties, and a property is made of parcels.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Why parcels exist at all',
        text: 'One building can sit on more than one parcel. A shopping center might be four parcels — the stores, the parking lot, and two strips of land at the edge. The government sends a separate value and a separate bill for each one, so you have to appeal each one separately. That is why the app insists on tracking parcels and not just addresses.',
      },
      {
        kind: 'heading',
        text: 'The rest of the chain',
      },
      {
        kind: 'text',
        text: 'Under a parcel sit the things that change every year:',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Parcel value', meaning: 'What something is said to be worth, for one tax year. There are several of these per parcel per year, because the number changes as the argument goes on — see the article on values.' },
          { term: 'Appeal', meaning: 'The argument itself, for one parcel in one year.' },
          { term: 'Tax bill', meaning: 'The demand for money that the collector sends out once the value is settled.' },
        ],
      },
      {
        kind: 'heading',
        text: 'Two more words you will see',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Parent client', meaning: 'A holding company or fund that owns several of your clients. Use it when one investor wants a single report covering everything they own across multiple companies.' },
          { term: 'Contact', meaning: 'A person you deal with — someone at the client, or someone at the assessor’s office. A contact is a human being. A client is a paying account. They are not the same record.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Turning something off turns off everything under it',
        text: 'Marking a client inactive also makes all their properties inactive, and all the parcels under those properties. It flows downhill. That is usually what you want when a client leaves, but it is a big action — check you meant it.',
      },
    ],
  },

  {
    slug: 'who-does-what-outside-your-firm',
    title: 'Assessors, collectors and jurisdictions',
    summary: 'Three different government offices, three different jobs. Mixing them up causes real mistakes.',
    category: 'basics',
    keywords: ['assessor', 'collector', 'jurisdiction', 'government', 'county', 'appraisal district', 'cad', 'authority', 'board', 'arb', 'who sets the value'],
    related: ['the-big-picture', 'what-is-an-appeal', 'tax-bills'],
    body: [
      {
        kind: 'text',
        text: 'Three separate government bodies touch every parcel, and they do genuinely different things.',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Assessor', meaning: 'Decides what the property is worth. In some states this office is called an appraisal district. This is who you argue with. If you disagree with a value, the assessor is your opponent.' },
          { term: 'Jurisdiction', meaning: 'A body that charges tax on the property — the county, the city, the school district, a hospital district, and so on. Each one sets its own tax rate. A single parcel usually pays several jurisdictions at once.' },
          { term: 'Collector', meaning: 'Sends the bill and takes the money. Often collects on behalf of several jurisdictions at once, so one bill can cover many of them.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'The order things happen in',
        text: 'The assessor sets a value. Every jurisdiction applies its rate to that value. The collector adds those up and sends one bill. So arguing the value down at the assessor reduces what every jurisdiction charges — which is why one successful appeal saves money on several lines at once.',
      },
      {
        kind: 'heading',
        text: 'Why the app keeps them apart',
      },
      {
        kind: 'text',
        text: 'Because the money depends on it. To work out what a reduction is worth you need each jurisdiction’s own rate, not a single blended number. A parcel that pays a county, a city and a school district has three rates, and they change at different times of year.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'One rate covering several jurisdictions is not the same as one jurisdiction',
        text: 'If your source data gives you a single combined rate for a parcel, do not spread that rate across each jurisdiction — that multiplies the tax by the number of jurisdictions. If you are importing data and you are not sure, stop and ask an admin before committing the import.',
      },
    ],
  },

  {
    slug: 'what-is-an-appeal',
    title: 'What an appeal is, and the stages it goes through',
    summary: 'An appeal is one argument about one parcel in one year. It moves through set stages, and the app tracks which one you are at.',
    category: 'basics',
    keywords: ['appeal', 'protest', 'lifecycle', 'stages', 'workflow', 'hearing', 'arb', 'board order', 'litigation', 'settled', 'withdrawn', 'status'],
    related: ['the-big-picture', 'the-appeals-queue', 'moving-an-appeal-forward'],
    body: [
      {
        kind: 'text',
        text: 'An appeal covers exactly one parcel for exactly one tax year. If a client owns twelve parcels and you argue about all of them, that is twelve appeals — not one.',
      },
      {
        kind: 'heading',
        text: 'The normal path',
      },
      {
        kind: 'text',
        text: 'Most appeals walk through these stages in order. Not every appeal hits every stage — many finish early, and that is a good outcome, not a skipped step.',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Value notice received', meaning: 'The assessor has told you their number for this year. The clock starts here.' },
          { term: 'Protest filed', meaning: 'You have formally said you disagree. This is the deadline that matters most — miss it and the year is over.' },
          { term: 'Hearing notice received', meaning: 'They have written back to say when they will hear you.' },
          { term: 'Hearing scheduled', meaning: 'The date is on the calendar.' },
          { term: 'Hearing resolved', meaning: 'The hearing happened, or you settled without one.' },
          { term: 'Awaiting board order', meaning: 'A decision was made but the official paperwork has not arrived.' },
          { term: 'Board order received', meaning: 'The paperwork is in. The value is now official.' },
          { term: 'Litigation window', meaning: 'A limited period in which you could take it to court if the decision was bad. It closes.' },
          { term: 'Invoiced', meaning: 'You have billed the client for the work.' },
          { term: 'Paid', meaning: 'They paid. Done.' },
        ],
      },
      {
        kind: 'text',
        text: 'Two stages sit outside that line. Litigation filed and litigation closed record the rarer cases that go to court. Withdrawn means you dropped the appeal — usually because the value turned out to be fair after all.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Settling is not the same as being paid',
        text: 'An appeal that reached a good value still owes you an invoice. That gap is exactly what the "Billable items" number on your dashboard is measuring. Work that stops at "hearing resolved" and never moves on is work you did for free.',
      },
      {
        kind: 'heading',
        text: 'Affidavit or attended?',
      },
      {
        kind: 'text',
        text: 'When an appeal resolves, the app asks how. An affidavit means you submitted your argument on paper and did not attend in person. Attended means somebody from your firm was there in person. Some fee agreements pay differently for the two, so answer it honestly.',
      },
    ],
  },

  {
    slug: 'what-is-a-parcel-value',
    title: 'Why one parcel has several values',
    summary: 'A parcel does not have one value. It has a set of them, because the number changes as the argument progresses, and the app keeps every version.',
    category: 'basics',
    keywords: ['value', 'values', 'assessed', 'market', 'proposed', 'final', 'arb', 'noticed value', 'value type', 'tax year', 'land', 'improvement'],
    related: ['the-big-picture', 'what-is-a-reduction', 'what-is-an-appeal'],
    body: [
      {
        kind: 'text',
        text: 'This trips up nearly everyone at first. A parcel does not store "the value". It stores a row for each version of the value, tagged with the tax year and what kind of value it is.',
      },
      {
        kind: 'text',
        text: 'That is on purpose. Your whole job is the difference between two of those numbers, so you cannot throw the old one away.',
      },
      {
        kind: 'heading',
        text: 'The kinds of value',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Proposed', meaning: 'The assessor’s opening number for the year — what they sent you in the notice, before you argued.' },
          { term: 'Final', meaning: 'The number everybody landed on once the argument finished.' },
          { term: 'ARB', meaning: 'A number set by an appraisal review board — a panel that hears the case when you and the assessor cannot agree.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Check which direction "proposed" points',
        text: 'In some source systems "proposed" means the assessor’s opening demand. In others it means the value your firm proposed. Those are opposites, and getting it backwards flips the sign of every saving you report. If proposed is consistently higher than the current value, it is the assessor’s number.',
      },
      {
        kind: 'heading',
        text: 'Land and improvement',
      },
      {
        kind: 'text',
        text: 'A value is usually split into two parts. Land is the dirt. Improvement is everything built on it — the building, the parking lot, the fencing. Added together they make the total. You will sometimes argue only one half, for instance when a building is in worse condition than the assessor thinks but the land value is fair.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Blank does not mean zero',
        text: 'An empty value box means nobody has told us the number yet. Zero means somebody is asserting the thing is genuinely worth nothing. The app keeps those apart, and so should you — typing 0 to mean "unknown" will show up later as a 100% reduction that never happened.',
      },
    ],
  },

  {
    slug: 'what-is-a-reduction',
    title: 'What a reduction is, and how your fee comes out of it',
    summary: 'A reduction is how much you got the value knocked down. It is not the same as the money saved, and your fee usually depends on the difference.',
    category: 'basics',
    keywords: ['reduction', 'savings', 'saved', 'fee', 'contingency', 'tax savings', 'value reduction', 'basis', 'how am i paid', 'commission'],
    related: ['what-is-a-parcel-value', 'fee-agreements', 'what-is-an-appeal'],
    body: [
      {
        kind: 'text',
        text: 'Two numbers sound the same and are not. Getting them the wrong way round is the most expensive mistake available in this app, so it is worth two minutes.',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Value reduction', meaning: 'How much you knocked off what the property is said to be worth. If the assessor said $5,000,000 and you agreed $4,500,000, the value reduction is $500,000.' },
          { term: 'Tax savings', meaning: 'How much actual tax the client no longer has to pay because of that. That is the value reduction multiplied by the tax rate — a much smaller number.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'These differ by a large multiple',
        text: 'Tax rates are a few percent. So a value reduction is often twenty to fifty times bigger than the tax saving it produces. A fee agreement that charges a percentage of the wrong one bills the client a wildly wrong amount. When you set up a fee, always check which basis it uses.',
      },
      {
        kind: 'heading',
        text: 'How firms usually charge',
      },
      {
        kind: 'bullets',
        items: [
          'Contingency — a percentage of what you saved. You only get paid if you win. This is the most common arrangement.',
          'Flat fee — an agreed amount per parcel, win or lose.',
          'A mix — a small flat fee plus a percentage of the saving.',
        ],
      },
      {
        kind: 'text',
        text: 'Whichever it is, the terms live on the client’s fee agreement, and the app uses them to work out the invoice for you. You should not be doing this arithmetic by hand.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Caps and minimums',
        text: 'Some agreements cap the fee, or set a floor under it. Some states also cap how much a value can rise year on year, which changes what counts as a saving. If a number looks wrong, look for a cap before you assume the app miscalculated.',
      },
    ],
  },

  {
    slug: 'the-tax-year',
    title: 'Tax years, and why last year is still on your screen',
    summary: 'Almost everything in the app is tagged with a tax year. Understanding that explains most "why am I seeing this" questions.',
    category: 'basics',
    keywords: ['tax year', 'year', 'current year', 'roll', 'year roll', 'season', 'annual', 'last year', 'history'],
    related: ['year-roll', 'what-is-a-parcel-value', 'the-calendar'],
    body: [
      {
        kind: 'text',
        text: 'Property tax runs on an annual cycle. Values are set once a year, appealed once a year, and billed once a year. So nearly every record here carries a tax year, and the same parcel will have a separate set of records for 2025, 2026 and 2027.',
      },
      {
        kind: 'text',
        text: 'Your organization has one current year, set by an admin. That is what new records default to and what most screens show first.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Seeing something unexpected? Check the year filter',
        text: 'Most lists have a year selector near the top. If a parcel looks empty, or a client seems to have lost all their appeals, you are very often just looking at the wrong year.',
      },
      {
        kind: 'text',
        text: 'Old years are never deleted. You need last year’s numbers to prove this year’s reduction, and to answer a client asking what you did for them in 2024.',
      },
      {
        kind: 'text',
        text: 'Moving the whole firm to a new year is a deliberate action called a Year Roll, which an admin runs once a season. It has its own article.',
      },
    ],
  },
];
